import type * as React from "react";
import { Badge } from "../../components/ui/badge";
import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import { cn } from "../../lib/utils";
import { useEntitySelector } from "./entity-selector-context";
import type { Omittable } from "../../lib/props";

export type EntitySelectorSelectedChipsProps = Omittable<{
  /** Hide the row entirely when nothing is selected. @default true */
  hideWhenEmpty?: boolean;
  /** Optional label shown before the chip row (e.g. "Selected"). */
  label?: string;
  /** Show the count badge next to the label. @default true */
  showCount?: boolean;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}>;

/**
 * Horizontally-scrolling row of removable chips, one per selection. Chips
 * display the entity title and an `x` icon that calls `toggleSelection` to
 * deselect. Pair this with `<EntitySelectorPool>` to get the panels UX
 * without splitting the list into separate columns.
 */
function EntitySelectorSelectedChips({
  hideWhenEmpty = true,
  label,
  showCount = true,
  className,
  ref,
}: EntitySelectorSelectedChipsProps) {
  const { selections, toggleSelection } = useEntitySelector();

  if (hideWhenEmpty && selections.length === 0) return null;

  return (
    <Box
      direction="row"
      align="center"
      gap="sm"
      border="bottom"
      paddingY="sm"
      className={cn("px-3", className)}
    >
      {label ? (
        <Box direction="row" align="center" gap="xs" shrink={false}>
          <Text size="xs" weight="medium" variant="muted">
            {label}
          </Text>
          {showCount && selections.length > 0 && (
            <Badge variant="secondary" size="sm">
              {selections.length}
            </Badge>
          )}
        </Box>
      ) : null}
      {selections.length === 0 ? (
        <Text size="sm" variant="muted">
          None
        </Text>
      ) : (
        <Box overflowX="auto"
          ref={ref}
          direction="row"
          align="center"
          gap="xs"
          flex={1}
          minWidth={0}
          className="[scrollbar-width:none]"
        >
          {selections.map((sel) => (
            <Badge key={`${sel.poolId}-${sel.id}`} size="sm" variant="secondary">
              <Box direction="row" align="center" gap="xs">
                <Text size="xs">{sel.entity.title}</Text>
                <Box
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    toggleSelection(sel.entity, sel.poolId);
                  }}
                  aria-label={`Remove ${sel.entity.title}`}
                >
                  <Icon name="x" size="xs" color="muted" />
                </Box>
              </Box>
            </Badge>
          ))}
        </Box>
      )}
    </Box>
  );
}

EntitySelectorSelectedChips.displayName = "EntitySelectorSelectedChips";

export { EntitySelectorSelectedChips };
